/**
 * useSwipeGesture Hook
 *
 * Lightweight swipe gesture detector using pointer events.
 * Supports up/down/left/right swipe detection with configurable
 * thresholds and velocity requirements.
 *
 * Uses passive listeners and requestAnimationFrame for performance
 * on low-end devices. No external gesture libraries required.
 *
 * Infrastructure-only — no domain logic.
 */

import { useRef, useEffect, useCallback } from 'react';

// ── Types ───────────────────────────────────────────────────────

export type SwipeDirection = 'up' | 'down' | 'left' | 'right';

export interface SwipeEvent {
  /** Direction of the swipe */
  direction: SwipeDirection;
  /** Distance swiped in pixels */
  distance: number;
  /** Velocity in px/ms */
  velocity: number;
  /** Duration of the swipe in ms */
  duration: number;
  /** Raw deltaX (positive = right) */
  deltaX: number;
  /** Raw deltaY (positive = down) */
  deltaY: number;
}

export interface SwipeGestureOptions {
  /** Minimum distance in px to register a swipe (default: 50) */
  threshold?: number;
  /** Minimum velocity in px/ms to register a swipe (default: 0.3) */
  velocityThreshold?: number;
  /** Maximum time in ms for the swipe to complete (default: 300) */
  maxDuration?: number;
  /** Directions to listen for (default: all) */
  directions?: SwipeDirection[];
  /** Called when a swipe is detected */
  onSwipe?: (event: SwipeEvent) => void;
  /** Called during active swiping with progress (0–1) */
  onSwiping?: (deltaX: number, deltaY: number) => void;
  /** Called when a touch/pointer starts */
  onSwipeStart?: () => void;
  /** Called when swiping ends (whether or not threshold was met) */
  onSwipeEnd?: () => void;
  /** Whether the gesture is currently enabled (default: true) */
  enabled?: boolean;
}

// ── Internal state ──────────────────────────────────────────────

interface SwipeState {
  startX: number;
  startY: number;
  startTime: number;
  tracking: boolean;
  pointerId: number | null;
}

// ── Hook ────────────────────────────────────────────────────────

/**
 * Attaches swipe gesture detection to a DOM element via ref.
 *
 * @example
 * ```tsx
 * function BottomSheet({ onClose }) {
 *   const swipeRef = useSwipeGesture({
 *     directions: ['down'],
 *     onSwipe: (e) => {
 *       if (e.direction === 'down') onClose();
 *     },
 *   });
 *
 *   return <div ref={swipeRef}>...</div>;
 * }
 * ```
 */
export function useSwipeGesture<T extends HTMLElement = HTMLElement>(
  options: SwipeGestureOptions = {},
): React.RefObject<T | null> {
  const elementRef = useRef<T | null>(null);
  const stateRef = useRef<SwipeState>({
    startX: 0,
    startY: 0,
    startTime: 0,
    tracking: false,
    pointerId: null,
  });

  // Store options in ref to avoid stale closures
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const handlePointerDown = useCallback((e: PointerEvent) => {
    const opts = optionsRef.current;
    if (opts.enabled === false) return;
    // Only track primary pointer (single-finger touch)
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    stateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startTime: Date.now(),
      tracking: true,
      pointerId: e.pointerId,
    };

    opts.onSwipeStart?.();
  }, []);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const state = stateRef.current;
    const opts = optionsRef.current;
    if (!state.tracking || e.pointerId !== state.pointerId) return;
    if (opts.enabled === false) return;

    const deltaX = e.clientX - state.startX;
    const deltaY = e.clientY - state.startY;

    // Use rAF for smooth updates
    requestAnimationFrame(() => {
      opts.onSwiping?.(deltaX, deltaY);
    });
  }, []);

  const handlePointerUp = useCallback((e: PointerEvent) => {
    const state = stateRef.current;
    const opts = optionsRef.current;
    if (!state.tracking || e.pointerId !== state.pointerId) return;

    state.tracking = false;
    state.pointerId = null;

    const deltaX = e.clientX - state.startX;
    const deltaY = e.clientY - state.startY;
    const duration = Date.now() - state.startTime;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    const threshold = opts.threshold ?? 50;
    const velocityThreshold = opts.velocityThreshold ?? 0.3;
    const maxDuration = opts.maxDuration ?? 300;
    const allowedDirections = opts.directions ?? ['up', 'down', 'left', 'right'];

    opts.onSwipeEnd?.();

    // Check if swipe meets minimum requirements
    if (duration > maxDuration) return;

    const isHorizontal = absDeltaX > absDeltaY;
    const distance = isHorizontal ? absDeltaX : absDeltaY;
    const velocity = distance / duration;

    if (distance < threshold || velocity < velocityThreshold) return;

    // Determine direction
    let direction: SwipeDirection;
    if (isHorizontal) {
      direction = deltaX > 0 ? 'right' : 'left';
    } else {
      direction = deltaY > 0 ? 'down' : 'up';
    }

    if (!allowedDirections.includes(direction)) return;

    opts.onSwipe?.({
      direction,
      distance,
      velocity,
      duration,
      deltaX,
      deltaY,
    });
  }, []);

  const handlePointerCancel = useCallback(() => {
    const state = stateRef.current;
    if (state.tracking) {
      state.tracking = false;
      state.pointerId = null;
      optionsRef.current.onSwipeEnd?.();
    }
  }, []);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    // Use passive listeners where possible for performance
    el.addEventListener('pointerdown', handlePointerDown, { passive: true });
    el.addEventListener('pointermove', handlePointerMove, { passive: true });
    el.addEventListener('pointerup', handlePointerUp, { passive: true });
    el.addEventListener('pointercancel', handlePointerCancel, { passive: true });

    return () => {
      el.removeEventListener('pointerdown', handlePointerDown);
      el.removeEventListener('pointermove', handlePointerMove);
      el.removeEventListener('pointerup', handlePointerUp);
      el.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel]);

  return elementRef;
}

export default useSwipeGesture;
